import { apiGatewayService, ApiType } from "services/api/ApiGatewayService";
import { formatIsoDate } from "utils/format";

export interface PassportDeviceInformation {
    manufacturer?: string;
    model?: string;
    imei?: string;
    imei_two?: string;
    serial?: string;
}

export interface PassportReport {
    last_processed?: string;
    device_information?: PassportDeviceInformation;
    clearance?: {
        blacklisted?: string;
        graylisted?: string;
        simlock?: string;
        lost_stolen_blocked?: string;
        find_my_iphone?: string;
        frp_status?: string;
        mdm_status?: string;
        abm_status?: string;
        tcg_locked?: string;
        autopilot_status?: string;
    };
    erasure_details?: {
        standard?: string;
        result?: string;
        date?: string;
        target?: {
            model?: string;
            type?: string;
            capacity?: string;
        };
    };
    diagnostic_result?: string;
}

export interface PublicPassportReport {
    last_processed?: string;
    device_information?: PassportDeviceInformation;
    erasure_result?: string;
    diagnostic_result?: string;
    clearance?: string;
}

export interface FetchPublicPassportResponse {
    reports: PublicPassportReport[];
    language?: string;
}

export interface FetchPassportsResponse {
    reports: PassportReport[];
}

interface FetchPassportsResponseDto {
    reports: PassportReport[];
}

interface CreatePassportLinkDto {
    token: string;
}

class PassportService {
    public fetchPassports(search: string, abortController?: AbortController): Promise<FetchPassportsResponse> {
        return apiGatewayService
            .invokeApi(`/api/v1/passports?search=${encodeURIComponent(search)}`, "GET", null, {
                abortSignal: abortController?.signal,
                refreshSession: true,
                apiType: ApiType.OLIVER,
            })
            .then((dto: FetchPassportsResponseDto) => ({
                reports: dto.reports,
            }));
    }

    public fetchSharedPassport(token: string, abortController?: AbortController): Promise<FetchPublicPassportResponse> {
        return apiGatewayService.invokeApi<null, FetchPublicPassportResponse>(
            `/api/v1/passports/shared?token=${encodeURIComponent(token)}`,
            "GET",
            null,
            {
                abortSignal: abortController?.signal,
                // Anyone can open the link, so there is no session to refresh and a failure must not log anyone out.
                refreshSession: false,
                includeCredentials: false,
                apiType: ApiType.OLIVER,
            }
        );
    }

    public createLink(searchValue: string, expiration: Date, languageCode: string): Promise<string> {
        return apiGatewayService
            .invokeApi(
                "/api/v1/passports/link",
                "POST",
                { search_value: searchValue, expiration: formatIsoDate(expiration), language: languageCode },
                { refreshSession: true, apiType: ApiType.OLIVER }
            )
            .then((dto: CreatePassportLinkDto) => dto.token);
    }
}

export const passportService = new PassportService();
